/**
 * `/crucible/version` probe + React hook.
 *
 * Companion to `me-probe.ts`. The runtime exposes `GET /crucible/version`
 * (lane y / Volley-Inc/crucible#1248) as an auth-less endpoint that
 * returns `{ runtimeVersion, mode }`. The SPA reads it on boot and
 * surfaces the result in a small operator-visible footer so:
 *
 *   - Operators verifying a deploy can see the running version at a
 *     glance (no devtools required)
 *   - The manual end-to-end runbook
 *     (`docs/cloud-vm/ops/staging-end-to-end-runbook.md`) can probe
 *     the SPA's `data-testid="version-info"` to confirm parity with
 *     the expected deployed version
 *   - A "Crucible v0.2.0 (alb)" footer is visible to support agents
 *     when triaging user-reported issues
 *
 * The probe is fire-and-forget — it doesn't gate the boot flow. If
 * the runtime is unreachable (e.g. the colocation infra isn't fully
 * wired yet), the footer renders a "version unknown" fallback rather
 * than blocking the SPA's render.
 *
 * Auth posture: the underlying endpoint is auth-less by design (the
 * colocation ALB gates `/crucible/*` at the edge). So the probe runs
 * BEFORE the user signs in — operators can see the version on a
 * signed-out shell, which is the common debug case.
 */

import { useEffect, useState } from 'react';

import type { HttpAuthMode } from './http-auth-types.js';

interface RuntimeVersionInfo {
  readonly runtimeVersion: string;
  readonly mode: HttpAuthMode;
}

export type VersionProbeResult =
  | { kind: 'ready'; info: RuntimeVersionInfo }
  | { kind: 'error'; reason: string };

export interface VersionProbeDependencies {
  fetch: typeof fetch;
  /** Defaults to `/crucible/version`. Tests override to assert. */
  probePath: string;
}

function defaultVersionProbeDependencies(): VersionProbeDependencies {
  return {
    fetch: globalThis.fetch.bind(globalThis),
    probePath: '/crucible/version',
  };
}

/**
 * Issue `GET /crucible/version` + decode the response. Never throws;
 * fetch failures + malformed bodies collapse to the `error` variant.
 *
 * Cookie-less probe — the endpoint is auth-less from the runtime's
 * perspective. We still pass `credentials: 'include'` so a cookie
 * (if the user is signed in) rides along — the ALB might use it for
 * audit logging.
 */
export async function probeVersion(
  deps: VersionProbeDependencies = defaultVersionProbeDependencies(),
): Promise<VersionProbeResult> {
  let res: Response;
  try {
    res = await deps.fetch(deps.probePath, {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
  } catch (err) {
    return {
      kind: 'error',
      reason: `version probe fetch failed: ${(err as Error).message}`,
    };
  }

  if (res.status !== 200) {
    return {
      kind: 'error',
      reason: `version probe returned unexpected status ${res.status}`,
    };
  }

  let rawBody: unknown;
  try {
    rawBody = await res.json();
  } catch {
    return {
      kind: 'error',
      reason: 'version probe response was not valid JSON',
    };
  }

  // Same null/object guard pattern as me-probe.ts (Bugbot Medium on
  // PR #1235 that we already fixed there).
  if (rawBody === null || typeof rawBody !== 'object') {
    return {
      kind: 'error',
      reason: 'version probe response was not a JSON object',
    };
  }

  const body = rawBody as {
    runtimeVersion?: unknown;
    mode?: unknown;
  };

  if (typeof body.runtimeVersion !== 'string' || body.runtimeVersion.length === 0) {
    return {
      kind: 'error',
      reason: 'version probe missing runtimeVersion',
    };
  }
  if (body.mode !== 'stub' && body.mode !== 'alb') {
    return {
      kind: 'error',
      reason: `version probe has invalid mode "${String(body.mode)}"`,
    };
  }

  return {
    kind: 'ready',
    info: { runtimeVersion: body.runtimeVersion, mode: body.mode },
  };
}

export type RuntimeVersionState =
  | { kind: 'loading' }
  | { kind: 'ready'; info: RuntimeVersionInfo }
  | { kind: 'error'; reason: string };

/**
 * React hook that fires `probeVersion()` once on mount and exposes
 * the result. Decoupled from `useCookieBoot` so the version footer
 * is available regardless of auth state (signed-in / signed-out /
 * boot-error).
 *
 * Empty dep array — runs exactly once per mount. A failed probe
 * stays failed for the session; if the operator wants a re-probe
 * they reload the page. Avoids retry storms on a busted runtime.
 */
export function useRuntimeVersion(
  deps: VersionProbeDependencies = defaultVersionProbeDependencies(),
): RuntimeVersionState {
  const [state, setState] = useState<RuntimeVersionState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void probeVersion(deps).then((result) => {
      if (cancelled) return;
      if (result.kind === 'ready') {
        setState({ kind: 'ready', info: result.info });
      } else {
        setState({ kind: 'error', reason: result.reason });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
